/**
 * Passkeys / Sicherheitsschluessel als zweiter Faktor (Plan 07 Paket 12).
 *
 * GoTrue fuehrt sie als Faktortyp `webauthn` neben `totp`. Ein verifizierter Passkey ist fuer
 * Tor, Datenbank-Policies (`infra_mfa_db_zugang`) und den OAuth-Schutz ein Faktor wie jeder
 * andere: sobald er nachgewiesen ist, steht die Sitzung auf `aal2`. Diese Datei haelt nur,
 * was Karte und Anmeldeschritt gemeinsam brauchen.
 *
 * **Bewusst KEIN Passkey-Login ohne Passwort.** GoTrue kennt WebAuthn nur als zweiten Faktor
 * (die Challenge verlangt eine bestehende Sitzung). Ein eigener Erstfaktor-Weg wuerde eine
 * `aal1`-Sitzung ausstellen — das Tor verlangte danach trotzdem einen zweiten Faktor, der
 * Passkey braechte also keine Ersparnis; mehr dazu in `docs/architecture/passkeys-und-sso.md`.
 */

export interface MfaFaktor {
  id: string;
  status: string;
  friendly_name?: string;
  factor_type?: string;
  created_at?: string;
}

interface ListFactorsDaten {
  totp?: MfaFaktor[];
  webauthn?: MfaFaktor[];
}

/** Verifizierte Passkeys. Halbfertige (`unverified`) zaehlen nicht — sie sind keine Faktoren. */
export function passkeysAus(daten: ListFactorsDaten | null | undefined): MfaFaktor[] {
  return (daten?.webauthn ?? []).filter((f) => f.status === "verified");
}

/** Verifizierte Authenticator-App-Faktoren (aeltere Tests liefern Faktoren ohne `factor_type`). */
export function appFaktorenAus(daten: ListFactorsDaten | null | undefined): MfaFaktor[] {
  return (daten?.totp ?? []).filter((f) => f.status === "verified" && (f.factor_type ?? "totp") === "totp");
}

export function browserKannPasskeys(): boolean {
  return typeof window !== "undefined" && typeof window.PublicKeyCredential === "function";
}

interface FehlerArt {
  name?: string;
  code?: string;
  message?: string;
  cause?: { name?: string } | null;
}

/**
 * Verstaendlicher Text zu einem Fehler aus der WebAuthn-Zeremonie oder von GoTrue.
 * Abbrechen im Systemdialog ist der haeufigste „Fehler" und kein Defekt — er bekommt keinen
 * roten Alarmton, sondern den Weg weiter.
 */
export function passkeyFehlerText(fehler: unknown): string {
  const f = (fehler ?? {}) as FehlerArt;
  const name = f.cause?.name ?? f.name ?? "";
  const m = (f.message ?? "").toLowerCase();
  if (name === "NotAllowedError" || /timed out or was not allowed|not allowed/.test(m)) {
    return "Abgebrochen oder abgelaufen. Du kannst es jederzeit noch einmal versuchen.";
  }
  if (f.code === "ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED" || name === "InvalidStateError") {
    return "Dieser Passkey ist für dein Konto schon eingerichtet.";
  }
  if (f.code === "mfa_webauthn_enroll_not_enabled" || /disabled for webauthn|webauthn.*disabled/.test(m)) {
    return "Passkeys sind auf diesem System noch nicht freigeschaltet.";
  }
  if (/does not support webauthn/.test(m)) {
    return "Dieser Browser unterstützt keine Passkeys.";
  }
  if (/friendly.?name.*(exist|unique|already)|already exists/.test(m)) {
    return "Diesen Namen gibt es schon. Bitte einen anderen Namen wählen.";
  }
  if (/aal2|assurance/.test(m)) {
    return "Bitte bestätige zuerst deinen zweiten Faktor und versuche es dann noch einmal.";
  }
  if (/rate|too many/.test(m)) {
    return "Zu viele Versuche. Bitte einen Moment warten und dann erneut versuchen.";
  }
  return f.message || "Das hat nicht geklappt. Bitte erneut versuchen.";
}

/** Vorschlag fuer den Namen eines neuen Passkeys: „Passkey 1", „Passkey 2" … — nie doppelt. */
export function naechsterPasskeyName(vorhanden: MfaFaktor[]): string {
  const belegt = new Set(vorhanden.map((f) => f.friendly_name));
  for (let n = vorhanden.length + 1; ; n++) {
    const name = `Passkey ${n}`;
    if (!belegt.has(name)) return name;
  }
}
